import { useState, useEffect, useCallback, useMemo } from 'react';
import { bannerApi } from '@/lib/api';
import { useAuthStore } from '@/store';
import { devLog, devWarn } from '@/lib/devLog';
import useDesktopBanners, { canShowDesktopBanners } from '@/hooks/useDesktopBanners';
import {
  pickActiveGlobalPlacement,
  filterBannersForPlacement,
  filterGoPlusBanners,
} from '@/lib/bannerPlacementLogic';

export {
  GLOBAL_EXCLUSIVE_PLACEMENTS,
  PLACEMENT_PRIORITY,
  pickActiveGlobalPlacement,
  filterBannersForPlacement,
} from '@/lib/bannerPlacementLogic';

let _globalBanners = [];
let _globalListeners = new Set();
let _fetchPromise = null;
let _seenThisSession = new Set();
let _lastFetchTime = 0;

const REFETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const EMPTY_BANNERS = [];

// ─── Cooldown system ─────────────────────────────────────────────────────────
// Prevents banners from appearing immediately after another is dismissed.
const COOLDOWN_MS = {
  modal: 300_000,           // 5 minutes before another modal can appear
  'home-banner': 15_000,
  'chat-banner': 15_000,
  sidebar: 15_000,
  'credit-store': 15_000,
  'upgrade-page': 15_000,
  _post_modal: 120_000,     // 2 minutes after modal dismiss → blocks tooltips + announcement bars
  _tooltip_anchor: 60_000,  // per-anchor cooldown for same-element tooltip
};

let _cooldowns = {};
let _cooldownTimers = {};

function setCooldown(key, durationMs) {
  _cooldowns[key] = Date.now() + durationMs;
  if (_cooldownTimers[key]) clearTimeout(_cooldownTimers[key]);
  _cooldownTimers[key] = setTimeout(() => {
    delete _cooldowns[key];
    delete _cooldownTimers[key];
    notifyListeners();
  }, durationMs);
}

export function isCoolingDown(key) {
  const expiresAt = _cooldowns[key];
  if (!expiresAt) return false;
  if (Date.now() >= expiresAt) {
    delete _cooldowns[key];
    return false;
  }
  return true;
}

function notifyListeners() {
  for (const listener of _globalListeners) {
    listener([..._globalBanners]);
  }
}

async function fetchBanners() {
  // Also guard explicit refetches triggered by purchases outside this hook.
  if (!canShowDesktopBanners()) return;

  const token = useAuthStore.getState().token;
  if (!token) {
    _globalBanners = [];
    notifyListeners();
    return;
  }

  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = (async () => {
    try {
      const banners = await bannerApi.getAvailable('web');
      const flags = useAuthStore.getState().user?.flags || {};
      _globalBanners = filterGoPlusBanners(banners, flags);
      _lastFetchTime = Date.now();
      devLog('[banners] Fetched', banners.length, 'banners');
      notifyListeners();
    } catch (err) {
      devWarn('[banners] Fetch error:', err.message);
    } finally {
      _fetchPromise = null;
    }
  })();

  return _fetchPromise;
}

function removeBannerLocally(bannerId) {
  _globalBanners = _globalBanners.filter(b => b.id !== bannerId);
  notifyListeners();
}

export function useUserBanners() {
  const enabled = useDesktopBanners();
  const [cachedBanners, setBanners] = useState(_globalBanners);
  // Hide synchronously on resize, including banners already cached or in flight.
  const banners = enabled ? cachedBanners : EMPTY_BANNERS;
  const token = useAuthStore(state => state.token);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const isComped = useAuthStore(state => state.user?.flags?.isComped);

  useEffect(() => {
    if (!enabled) return;

    const listener = (updated) => setBanners(updated);
    _globalListeners.add(listener);
    setBanners(_globalBanners);

    if (!token) {
      _globalBanners = [];
      setBanners([]);
    } else if (Date.now() - _lastFetchTime > REFETCH_INTERVAL_MS || !_globalBanners.length) {
      fetchBanners();
    }

    return () => {
      _globalListeners.delete(listener);
    };
  }, [token, enabled]);

  // Plus/comped can flip without a token change (purchase, admin grant).
  // Re-strip the in-memory cache so a stale COMEBACK50 cannot linger.
  useEffect(() => {
    const next = filterGoPlusBanners(_globalBanners, { isPlus, isPlusPremium, isComped });
    if (next.length !== _globalBanners.length) {
      _globalBanners = next;
      notifyListeners();
    }
  }, [isPlus, isPlusPremium, isComped]);

  const activeGlobalPlacement = useMemo(
    () => pickActiveGlobalPlacement(banners, isCoolingDown),
    [banners],
  );

  const getBannersForPlacement = useCallback((placement) => {
    return filterBannersForPlacement(banners, placement, activeGlobalPlacement, isCoolingDown);
  }, [banners, activeGlobalPlacement]);

  const getTooltipBanner = useCallback((anchorId) => {
    if (activeGlobalPlacement !== 'tooltip') return null;
    if (isCoolingDown('_post_modal')) return null;
    if (isCoolingDown(`tooltip:${anchorId}`)) return null;
    return banners.find(b => b.placement === 'tooltip' && b.tooltipAnchor === anchorId) || null;
  }, [banners, activeGlobalPlacement]);

  const markSeen = useCallback(async (bannerId) => {
    if (!canShowDesktopBanners()) return;
    if (_seenThisSession.has(bannerId)) return;
    _seenThisSession.add(bannerId);

    const banner = _globalBanners.find(b => b.id === bannerId);
    try {
      const result = await bannerApi.markSeen(bannerId, banner?._variant || null);

      if (banner?.countdown?.enabled && banner?._isFirstImpression && result.firstSeenAt) {
        const idx = _globalBanners.findIndex(b => b.id === bannerId);
        if (idx !== -1 && _globalBanners[idx].countdown.enabled && !_globalBanners[idx].countdown.endsAt) {
          _globalBanners[idx] = {
            ..._globalBanners[idx],
            _isFirstImpression: false,
          };
          notifyListeners();
        }
      }
    } catch (err) {
      devWarn('[banners] markSeen error:', err.message);
      _seenThisSession.delete(bannerId);
    }
  }, []);

  const markClicked = useCallback(async (bannerId) => {
    const banner = _globalBanners.find(b => b.id === bannerId);
    try {
      await bannerApi.markClicked(bannerId, banner?._variant || null);
    } catch (err) {
      devWarn('[banners] markClicked error:', err.message);
    }
  }, []);

  const dismiss = useCallback(async (bannerId) => {
    const banner = _globalBanners.find(b => b.id === bannerId);
    removeBannerLocally(bannerId);

    if (banner) {
      const p = banner.placement;
      if (p === 'tooltip') {
        if (banner.tooltipAnchor) {
          setCooldown(`tooltip:${banner.tooltipAnchor}`, COOLDOWN_MS._tooltip_anchor);
        }
      } else {
        setCooldown(p, COOLDOWN_MS[p] || 15_000);
      }
      if (p === 'modal') {
        setCooldown('_post_modal', COOLDOWN_MS._post_modal);
      }
      devLog('[banners] cooldown set:', p, banner.tooltipAnchor || '');
    }

    try {
      await bannerApi.dismiss(bannerId);
    } catch (err) {
      devWarn('[banners] dismiss error:', err.message);
    }
  }, []);

  const refetch = useCallback(() => {
    return refetchBanners();
  }, []);

  return {
    banners,
    getBannersForPlacement,
    getTooltipBanner,
    markSeen,
    markClicked,
    dismiss,
    refetch,
  };
}

export function refetchBanners() {
  _lastFetchTime = 0;
  return fetchBanners();
}

export function clearBannerCache() {
  _globalBanners = [];
  _seenThisSession.clear();
  _lastFetchTime = 0;
  _fetchPromise = null;
  for (const key of Object.keys(_cooldownTimers)) clearTimeout(_cooldownTimers[key]);
  _cooldowns = {};
  _cooldownTimers = {};
  notifyListeners();
}
